import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Badge,
    Button,
    Col,
    Container,
    Form,
    Modal,
    Row,
    Spinner,
    Table,
} from 'react-bootstrap';
import Select from 'react-select';
import debounce from 'lodash.debounce';
import PageTitle from '../../Components/PageTitle/PageTitle.js';
import TargetSpecialtiesSelector from '../../Components/Common/Inputs/TargetSpecialtiesSelector';
import MultiSelectCheckboxDropdown from '../../Components/Common/Inputs/MultiSelectCheckboxDropdown';
import { useGlobalContext } from '../../Context/index';
import {
    ActiveAdForConfig,
    AdsAudience,
    AdsConfigLimits,
    DegreeOption,
    UsertypeOption,
    deleteAdsConfig,
    emptyLimits,
    getActiveAdsForConfig,
    getDegrees,
    getUsertypes,
    summarizeLimits,
    upsertAdsConfig,
} from './api';
import './AdsConfig.css';

type SelectOption = { value: string; label: string };

const selectMenuPortalProps = {
    menuPortalTarget: document.body,
    styles: {
        menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 9999 }),
    },
};

const audienceOptions: SelectOption[] = [
    { value: '', label: 'All' },
    { value: 'doctor', label: 'Doctors only' },
    { value: 'student', label: 'Students only' },
];

const AdsConfigPage: React.FC = () => {
    const { setShowSuccessToast, setShowErrorToast } = useGlobalContext();

    const [ads, setAds] = useState<ActiveAdForConfig[]>([]);
    const [usertypes, setUsertypes] = useState<UsertypeOption[]>([]);
    const [degrees, setDegrees] = useState<DegreeOption[]>([]);
    const [sections, setSections] = useState<string[]>([]);
    const [sectionFilter, setSectionFilter] = useState<SelectOption | null>(null);
    const [searchInput, setSearchInput] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [error, setError] = useState('');

    const [editing, setEditing] = useState<ActiveAdForConfig | null>(null);
    const [limits, setLimits] = useState<AdsConfigLimits>(emptyLimits());
    const [formError, setFormError] = useState('');

    const sectionOptions = useMemo(
        () => sections.map(section => ({ value: section, label: section })),
        [sections],
    );

    const usertypeOptions = useMemo(
        () => usertypes.map(u => ({ value: u.usertypeid, label: u.name })),
        [usertypes],
    );

    const degreeOptions = useMemo(
        () => degrees.map(d => ({ value: d.degreeid, label: d.name })),
        [degrees],
    );

    const fetchMeta = useCallback(async () => {
        try {
            const [usertypeRows, degreeRows] = await Promise.all([
                getUsertypes(),
                getDegrees(),
            ]);
            setUsertypes(usertypeRows);
            setDegrees(degreeRows);
        } catch {
            // non-blocking for list view
        }
    }, []);

    const fetchAds = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const data = await getActiveAdsForConfig({
                section: sectionFilter?.value,
                name: searchQuery,
            });
            const rows = Array.isArray(data) ? data : [];
            setAds(rows);
            const uniqueSections = Array.from(
                new Set(rows.map(r => r.section).filter(Boolean)),
            ).sort();
            setSections(prev =>
                Array.from(new Set([...prev, ...uniqueSections])).sort(),
            );
        } catch (err: unknown) {
            const message =
                err instanceof Error ? err.message : 'Failed to load ads';
            setError(message);
            setAds([]);
        } finally {
            setLoading(false);
        }
    }, [sectionFilter, searchQuery]);

    useEffect(() => {
        fetchMeta();
    }, [fetchMeta]);

    useEffect(() => {
        fetchAds();
    }, [fetchAds]);

    const debouncedSetSearch = useMemo(
        () =>
            debounce((value: string) => {
                setSearchQuery(value.trim());
            }, 400),
        [],
    );

    useEffect(
        () => () => debouncedSetSearch.cancel(),
        [debouncedSetSearch],
    );

    const openEdit = (ad: ActiveAdForConfig) => {
        setEditing(ad);
        setLimits({
            ...emptyLimits(),
            ...(ad.config?.limits || {}),
            specialties: ad.config?.limits?.specialties ?? [],
            degrees: ad.config?.limits?.degrees ?? [],
            usertypes: ad.config?.limits?.usertypes ?? [],
            hideAicpFaculty: !!ad.config?.limits?.hideAicpFaculty,
            cta_clicks: ad.config?.limits?.cta_clicks ?? ad.config?.cta_clicks ?? null,
            views: ad.config?.limits?.views ?? ad.config?.views ?? null,
        });
        setFormError('');
    };

    const closeEdit = () => {
        if (actionLoading) return;
        setEditing(null);
        setFormError('');
    };

    const handleSave = async () => {
        if (!editing) return;
        setActionLoading(true);
        setFormError('');
        try {
            const payload: AdsConfigLimits = {
                creatorsOnly: !!limits.creatorsOnly,
                testUsersOnly: !!limits.testUsersOnly,
                audience: limits.audience || null,
                specialties: limits.specialties ?? [],
                degrees: limits.degrees ?? [],
                usertypes: limits.usertypes ?? [],
                hideAicpFaculty: !!limits.hideAicpFaculty,
                cta_clicks: limits.cta_clicks,
                views: limits.views,
            };
            await upsertAdsConfig({ ad_id: editing.ad_id, limits: payload });
            setShowSuccessToast(true);
            setEditing(null);
            await fetchAds();
        } catch (err: unknown) {
            const message =
                err instanceof Error ? err.message : 'Failed to save config';
            setFormError(message);
            setShowErrorToast(true);
        } finally {
            setActionLoading(false);
        }
    };

    const handleReset = async () => {
        if (!editing) return;
        setActionLoading(true);
        setFormError('');
        try {
            await deleteAdsConfig(editing.ad_id);
            setShowSuccessToast(true);
            setEditing(null);
            await fetchAds();
        } catch (err: unknown) {
            const message =
                err instanceof Error ? err.message : 'Failed to reset config';
            setFormError(message);
            setShowErrorToast(true);
        } finally {
            setActionLoading(false);
        }
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchInput(value);
        if (!value.trim()) {
            debouncedSetSearch.cancel();
            setSearchQuery('');
        } else {
            debouncedSetSearch(value);
        }
    };

    return (
        <Container fluid className="ads-config-page" style={{ padding: '20px' }}>
            <Row className="align-items-center mb-3">
                <Col>
                    <PageTitle>Ad Targeting Config</PageTitle>
                    <p className="text-muted mb-0 small">
                        Configure who sees each active creative from Generic Ads.
                    </p>
                </Col>
            </Row>

            <Row className="align-items-center g-2 mb-3">
                <Col md={4}>
                    <Form.Control
                        type="search"
                        placeholder="Search by ads_gen name..."
                        value={searchInput}
                        onChange={handleSearchChange}
                    />
                </Col>
                <Col md={3}>
                    <Select
                        isClearable
                        placeholder="Filter section..."
                        options={sectionOptions}
                        value={sectionFilter}
                        onChange={opt => setSectionFilter(opt as SelectOption | null)}
                        {...selectMenuPortalProps}
                    />
                </Col>
                <Col md="auto">
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={fetchAds}
                        disabled={loading}
                    >
                        Refresh
                    </Button>
                </Col>
            </Row>

            {error && <Alert variant="danger">{error}</Alert>}

            {loading ? (
                <div className="d-flex justify-content-center py-5">
                    <Spinner animation="border" />
                </div>
            ) : (
                <div className="table-responsive ads-config-table-wrap">
                    <Table hover bordered size="sm" className="align-middle">
                        <thead>
                            <tr>
                                <th style={{ width: 72 }}>Banner</th>
                                <th style={{ width: '32%' }}>Creative</th>
                                <th style={{ width: '22%' }}>Section / Group</th>
                                <th>Targeting</th>
                                <th className="ads-config-actions">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ads.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="text-center text-muted py-4">
                                        No active ads found.
                                    </td>
                                </tr>
                            )}
                            {ads.map(ad => {
                                const allowall = ad.config?.allowall !== false;
                                const summary = summarizeLimits(
                                    ad.config?.limits,
                                    allowall,
                                    ad.config,
                                );
                                const isUnrestricted =
                                    summary === 'All users';
                                return (
                                    <tr key={ad.ad_id}>
                                        <td>
                                            {ad.banner ? (
                                                <img
                                                    src={ad.banner}
                                                    alt=""
                                                    className="ads-config-thumb"
                                                />
                                            ) : (
                                                <span className="text-muted">—</span>
                                            )}
                                        </td>
                                        <td>
                                            <div className="fw-semibold">
                                                {ad.displayName || 'Untitled'}
                                            </div>
                                            <div className="text-muted small font-monospace ads-config-id">
                                                {ad.ad_id}
                                            </div>
                                        </td>
                                        <td>
                                            <div>{ad.section}</div>
                                            <div className="text-muted small">
                                                {ad.ads_gen_name}
                                            </div>
                                        </td>
                                        <td>
                                            {isUnrestricted ? (
                                                <Badge bg="secondary">All users</Badge>
                                            ) : (
                                                <Badge bg="primary" className="text-wrap text-start">
                                                    {summary}
                                                </Badge>
                                            )}
                                        </td>
                                        <td className="ads-config-actions">
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={() => openEdit(ad)}
                                            >
                                                Configure
                                            </Button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </div>
            )}

            <Modal show={!!editing} onHide={closeEdit} size="lg" backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>
                        Configure targeting
                        {editing?.displayName ? ` — ${editing.displayName}` : ''}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {editing && (
                        <>
                            <div className="mb-3 small text-muted">
                                <div>
                                    <strong>Ad ID:</strong>{' '}
                                    <span className="font-monospace">{editing.ad_id}</span>
                                </div>
                                <div>
                                    <strong>Section:</strong> {editing.section} ·{' '}
                                    <strong>Group:</strong> {editing.ads_gen_name}
                                </div>
                            </div>

                            {formError && <Alert variant="danger">{formError}</Alert>}

                            <Form.Check
                                type="switch"
                                id="ads-config-creators"
                                label="Only show to creators"
                                className="mb-2"
                                checked={!!limits.creatorsOnly}
                                disabled={actionLoading}
                                onChange={e =>
                                    setLimits(prev => ({
                                        ...prev,
                                        creatorsOnly: e.target.checked,
                                    }))
                                }
                            />

                            <Form.Check
                                type="switch"
                                id="ads-config-test"
                                label="Only show to test users"
                                className="mb-2"
                                checked={!!limits.testUsersOnly}
                                disabled={actionLoading}
                                onChange={e =>
                                    setLimits(prev => ({
                                        ...prev,
                                        testUsersOnly: e.target.checked,
                                    }))
                                }
                            />

                            <Form.Check
                                type="switch"
                                id="ads-config-hide-aicp"
                                label="Hide for AICP Faculty?"
                                className="mb-3"
                                checked={!!limits.hideAicpFaculty}
                                disabled={actionLoading}
                                onChange={e =>
                                    setLimits(prev => ({
                                        ...prev,
                                        hideAicpFaculty: e.target.checked,
                                    }))
                                }
                            />

                            <Form.Group className="mb-3">
                                <Form.Label>Show to students or doctors?</Form.Label>
                                <Select
                                    options={audienceOptions}
                                    value={
                                        audienceOptions.find(
                                            o => o.value === (limits.audience || ''),
                                        ) || audienceOptions[0]
                                    }
                                    onChange={opt =>
                                        setLimits(prev => ({
                                            ...prev,
                                            audience: (opt?.value || null) as AdsAudience,
                                        }))
                                    }
                                    isDisabled={actionLoading}
                                    {...selectMenuPortalProps}
                                />
                            </Form.Group>

                            <MultiSelectCheckboxDropdown
                                idPrefix="ads-config-usertype"
                                title="Show to certain usertype"
                                note="*Leave as All to not restrict by usertype*"
                                itemNoun="usertype"
                                options={usertypeOptions}
                                value={limits.usertypes || []}
                                disabled={actionLoading}
                                onChange={(ids: string[]) =>
                                    setLimits(prev => ({ ...prev, usertypes: ids }))
                                }
                            />

                            <MultiSelectCheckboxDropdown
                                idPrefix="ads-config-degree"
                                title="Show to chosen degrees"
                                note="*Leave as All to not restrict by degree*"
                                itemNoun="degree"
                                options={degreeOptions}
                                value={limits.degrees || []}
                                disabled={actionLoading}
                                onChange={(ids: string[]) =>
                                    setLimits(prev => ({ ...prev, degrees: ids }))
                                }
                            />

                            <TargetSpecialtiesSelector
                                disabled={actionLoading}
                                value={limits.specialties || []}
                                onChange={(specialties: string[]) =>
                                    setLimits(prev => ({ ...prev, specialties }))
                                }
                                title="Show to chosen specialties"
                                note="*Leave as All to not restrict by specialty*"
                            />

                            <hr className="my-3" />
                            <h6 className="mb-2">Frequency caps</h6>
                            <p className="text-muted small mb-3">
                                Leave blank for no cap
                            </p>

                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>CTA clicks — max (global)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            min={0}
                                            placeholder="No limit"
                                            disabled={actionLoading}
                                            value={
                                                limits.cta_clicks?.max != null
                                                    ? String(limits.cta_clicks.max)
                                                    : ''
                                            }
                                            onChange={e => {
                                                const raw = e.target.value;
                                                setLimits(prev => {
                                                    const next = {
                                                        ...(prev.cta_clicks || {}),
                                                    };
                                                    if (raw === '') delete next.max;
                                                    else next.max = Number(raw);
                                                    const cleaned =
                                                        next.max == null &&
                                                            next.max_user == null
                                                            ? null
                                                            : next;
                                                    return {
                                                        ...prev,
                                                        cta_clicks: cleaned,
                                                    };
                                                });
                                            }}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>CTA clicks — max per user</Form.Label>
                                        <Form.Control
                                            type="number"
                                            min={0}
                                            placeholder="No limit"
                                            disabled={actionLoading}
                                            value={
                                                limits.cta_clicks?.max_user != null
                                                    ? String(limits.cta_clicks.max_user)
                                                    : ''
                                            }
                                            onChange={e => {
                                                const raw = e.target.value;
                                                setLimits(prev => {
                                                    const next = {
                                                        ...(prev.cta_clicks || {}),
                                                    };
                                                    if (raw === '') delete next.max_user;
                                                    else next.max_user = Number(raw);
                                                    const cleaned =
                                                        next.max == null &&
                                                            next.max_user == null
                                                            ? null
                                                            : next;
                                                    return {
                                                        ...prev,
                                                        cta_clicks: cleaned,
                                                    };
                                                });
                                            }}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Views — max (global)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            min={0}
                                            placeholder="No limit"
                                            disabled={actionLoading}
                                            value={
                                                limits.views?.max != null
                                                    ? String(limits.views.max)
                                                    : ''
                                            }
                                            onChange={e => {
                                                const raw = e.target.value;
                                                setLimits(prev => {
                                                    const next = {
                                                        ...(prev.views || {}),
                                                    };
                                                    if (raw === '') delete next.max;
                                                    else next.max = Number(raw);
                                                    const cleaned =
                                                        next.max == null &&
                                                            next.max_user == null
                                                            ? null
                                                            : next;
                                                    return {
                                                        ...prev,
                                                        views: cleaned,
                                                    };
                                                });
                                            }}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Views — max per user</Form.Label>
                                        <Form.Control
                                            type="number"
                                            min={0}
                                            placeholder="No limit"
                                            disabled={actionLoading}
                                            value={
                                                limits.views?.max_user != null
                                                    ? String(limits.views.max_user)
                                                    : ''
                                            }
                                            onChange={e => {
                                                const raw = e.target.value;
                                                setLimits(prev => {
                                                    const next = {
                                                        ...(prev.views || {}),
                                                    };
                                                    if (raw === '') delete next.max_user;
                                                    else next.max_user = Number(raw);
                                                    const cleaned =
                                                        next.max == null &&
                                                            next.max_user == null
                                                            ? null
                                                            : next;
                                                    return {
                                                        ...prev,
                                                        views: cleaned,
                                                    };
                                                });
                                            }}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Alert variant="light" className="mt-3 mb-0 small border">
                                Allow filters are OR-matched.
                                Clear all filters (or Reset) to show the ad to everyone.
                            </Alert>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer className="justify-content-between">
                    <Button
                        variant="outline-danger"
                        onClick={handleReset}
                        disabled={actionLoading || !editing}
                    >
                        Reset (allow all)
                    </Button>
                    <div className="d-flex gap-2">
                        <Button
                            variant="secondary"
                            onClick={closeEdit}
                            disabled={actionLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleSave}
                            disabled={actionLoading || !editing}
                        >
                            {actionLoading ? 'Saving…' : 'Save'}
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default AdsConfigPage;
